import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["step", "conditionalFields", "industrySelect"]
  static values = {
    applicationId: String,
    steps: Array,        // Step order from StepConfiguration::STEPS
    currentStep: String, // Current step from server
    skipVerifyCode: Boolean // Whether to skip verify_code step (email already verified)
  }

  connect() {
    // Listen for auto-save events
    this.boundAutoSaveSuccess = this.handleAutoSaveSuccess.bind(this)
    this.boundAutoSaveError = this.handleAutoSaveError.bind(this)

    document.addEventListener('auto-save:saved', this.boundAutoSaveSuccess)
    document.addEventListener('auto-save:error', this.boundAutoSaveError)

    // Scroll to top when a new step renders inside the Turbo Frame
    this.boundScrollToTop = this.scrollToTopOnStepChange.bind(this)
    const frame = document.querySelector('turbo-frame#application_step')
    if (frame) {
      frame.addEventListener('turbo:frame-render', this.boundScrollToTop)
    }

    // Initialize conditional fields based on current industry selection
    if (this.hasIndustrySelectTarget) {
      this.toggleConditionalFields()
    }
  }

  disconnect() {
    // Clean up event listeners
    document.removeEventListener('auto-save:saved', this.boundAutoSaveSuccess)
    document.removeEventListener('auto-save:error', this.boundAutoSaveError)

    const frame = document.querySelector('turbo-frame#application_step')
    if (frame) {
      frame.removeEventListener('turbo:frame-render', this.boundScrollToTop)
    }
  }

  toggleConditionalFields() {
    const selectedIndustry = this.industrySelectTarget.value

    if (selectedIndustry === 'Restaurant') {
      this.conditionalFieldsTarget.classList.remove('hidden-conditional')
      this.conditionalFieldsTarget.classList.add('show-conditional')
      this.conditionalFieldsTarget.style.display = 'block'
    } else {
      this.conditionalFieldsTarget.classList.remove('show-conditional')
      this.conditionalFieldsTarget.classList.add('hidden-conditional')
      this.conditionalFieldsTarget.style.display = 'none'
    }
  }

  previousStep(event) {
    event.preventDefault()

    const currentStep = this.getCurrentStepFromPage()
    const previousStep = this.getPreviousStep(currentStep)

    if (!previousStep) {
      console.log('Cannot determine previous step for:', currentStep)
      return
    }

    // Find the main application form on the page (not button_to forms)
    // Look for the form with the loan-form controller or the main application form
    const existingForm = document.querySelector('form[data-controller*="loan-form"]') ||
                         document.querySelector('form[action$="/application/' + this.applicationIdValue + '"]') ||
                         document.querySelector('form[action*="secure/app"]')

    if (existingForm) {
      // Update or add the current_step field to go to previous step
      let stepInput = existingForm.querySelector('input[name="loan_application[current_step]"]') ||
                      existingForm.querySelector('input[name="application[current_step]"]')

      if (stepInput) {
        stepInput.value = previousStep
      } else {
        // Create the input if it doesn't exist
        stepInput = document.createElement('input')
        stepInput.type = 'hidden'
        stepInput.name = 'loan_application[current_step]'
        stepInput.value = previousStep
        existingForm.appendChild(stepInput)
      }

      // Signal backwards navigation so loan_form_controller skips validation
      let goingBackInput = existingForm.querySelector('input[name="going_backwards"]')
      if (!goingBackInput) {
        goingBackInput = document.createElement('input')
        goingBackInput.type = 'hidden'
        goingBackInput.name = 'going_backwards'
        existingForm.appendChild(goingBackInput)
      }
      goingBackInput.value = 'true'

      // Use requestSubmit so Turbo intercepts and navigates within the frame
      existingForm.requestSubmit()
    } else {
      console.error('No form found to submit previous step')
    }
  }

  getPreviousStep(currentStep) {
    const steps = this.stepsValue

    if (!steps || steps.length === 0) {
      console.warn('No steps configuration found, falling back to detection')
      return this.getPreviousStepFallback(currentStep)
    }

    const currentIndex = steps.indexOf(currentStep)

    if (currentIndex <= 0) {
      return null
    }

    const prevStep = steps[currentIndex - 1]
    if (prevStep === "verify_code" && this.skipVerifyCodeValue) {
      return steps[currentIndex - 2]
    }
    return prevStep
  }

  getPreviousStepFallback(currentStep) {
    const defaultSteps = [
      'prequalification',
      'business_information',
      'business_details',
      'personal_information',
      'verify_code',
      'about_you',
      'revenue_verification',
      'submit'
    ]

    const currentIndex = defaultSteps.indexOf(currentStep)
    if (currentIndex <= 0) return null
    return defaultSteps[currentIndex - 1]
  }

  getCurrentStepFromPage() {
    // Prefer hidden input — always up-to-date after Turbo Frame updates,
    // whereas the Stimulus value on the outer wrapper may be stale.
    const currentStepInput = document.querySelector('input[name="loan_application[current_step]"]') ||
                             document.querySelector('input[name="application[current_step]"]') ||
                             document.querySelector('#application_current_step')
    if (currentStepInput && currentStepInput.value) {
      return currentStepInput.value
    }

    // Fall back to Stimulus value
    if (this.hasCurrentStepValue && this.currentStepValue) {
      return this.currentStepValue
    }

    // Try data attributes on step elements
    const stepElement = document.querySelector('[data-step]')
    if (stepElement) {
      return stepElement.dataset.step
    }

    // Try data-current-step attribute
    const stepIndicator = document.querySelector('[data-current-step]')
    if (stepIndicator) {
      return stepIndicator.dataset.currentStep
    }

    // Look for step content on the page to determine current step
    const steps = this.stepsValue || []
    for (const step of steps) {
      const stepId = `#${step.replace(/_/g, '-')}-step`
      if (document.querySelector(stepId)) {
        return step
      }
    }

    // Default fallback
    console.log('Could not determine current step')
    return 'prequalification'
  }

  scrollToTopOnStepChange() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  handleAutoSaveSuccess(event) {
    // Could add UI feedback here if needed
  }

  handleAutoSaveError(event) {
    console.error('Auto-save failed:', event.detail)
    // Could show error notification to user
  }

  saveAndExit(event) {
    event.preventDefault()

    // Find the auto-save controller and trigger save
    const autoSaveController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller*="auto-save"]'),
      'auto-save'
    )

    if (autoSaveController) {
      autoSaveController.saveAndExit()
    } else {
      // Fallback: redirect immediately if no auto-save controller
      console.warn('No auto-save controller found, redirecting without save')
      window.location.href = '/'
    }
  }

}
